/**
 * authWall — the photographs behind sign-in and the marketing hero
 * (AuthCollageBackground), and the two rules they follow. Rennan, Sep 29:
 *
 *   1. Every visit shows them in a new random order.
 *   2. The same photograph is never on screen twice at the same time.
 *
 * Portrait photographs only (his call: the wall's tiles are portrait, and
 * a landscape frame cropped to portrait loses its subject).
 *
 * Each entry below is one photograph, or a set of look-alikes (the
 * black-and-white copy of a frame, the next frame of a burst, the same
 * moment a second later). Only one member of a set is picked per visit,
 * so two versions of one moment never sit side by side either.
 *
 * Files: public/marketing/wall/<id>.jpg, 600×800, cropped to the tile's
 * 3:4. The iOS sign-in uses the same set from Assets.xcassets/AuthWall
 * (LoginView.swift, `AuthWall`) — add or remove a photo in both.
 */

export const AUTH_WALL_PHOTOS: readonly (readonly string[])[] = [
  ['1A6A2555'],
  ['517A2605'],
  ['517A5974'],
  ['BF8A0026'],
  ['BF8A0175'],
  ['BF8A0192', 'BF8A0206'],
  ['BF8A0308'],
  ['BF8A0392'],
  ['BF8A0413'],
  ['BF8A0438'],
  ['BF8A0481'],
  ['BF8A0572'],
  ['BF8A0621', 'BF8A0642'],
  ['BF8A0878', 'BF8A0894'],
  ['BF8A2711'],
  ['BF8A2725'],
  ['BF8A2745'],
  ['BF8A2808'],
  ['BF8A2825'],
  ['BF8A2836'],
  ['BF8A2970'],
  ['BF8A3382'],
  ['BF8A3469'],
  ['BF8A3721'],
  ['BF8A3940'],
  ['BF8A4018'],
  ['BF8A4038'],
  ['BF8A4063'],
  ['BF8A4092', 'BF8A4092-BW', 'BF8A4093'],
  ['BF8A4109'],
  ['BF8A4121'],
  ['BF8A4122'],
  ['BF8A4130'],
  ['BF8A4200', 'BF8A4201'],
  ['BF8A4451'],
  ['BF8A4461'],
  ['BF8A4527'],
  ['BF8A4852'],
  ['BF8A4962'],
  ['BF8A5532'],
  ['BF8A5704'],
  ['BF8A6572'],
  ['BF8A7371'],
  ['BF8A7599'],
  ['BF8A8625'],
  ['BF8A8882'],
  ['BF8A9032'],
  ['BF8A9364'],
];

export const authWallUrl = (id: string) => `/marketing/wall/${id.toLowerCase()}.jpg`;

/** One photograph from each look-alike set, in a fresh random order. */
export function shuffleWallPhotos(random: () => number = Math.random): string[] {
  const picked = AUTH_WALL_PHOTOS.map((set) => set[Math.floor(random() * set.length)]);
  for (let i = picked.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [picked[i], picked[j]] = [picked[j], picked[i]];
  }
  return picked;
}

/** The wall's geometry, in CSS px: side padding, column gap, tile gap, tile height ÷ width. */
export const WALL_GEOMETRY = { pad: 8, gap: 8, tileRatio: 4 / 3 } as const;

export interface WallPlan {
  columns: number;
  perColumn: number;
}

/**
 * How many columns, and how many photographs in each, for a wall this
 * size — so that no photograph can be seen twice at once.
 *
 * Each column loops a strip of `perColumn` photos shown twice (the
 * second copy makes the loop seamless). A photo's two copies are one
 * strip apart, so if a strip is longer than the wall plus one tile, the
 * copy is always off screen before the photo comes round again. One
 * spare tile covers the moment between a resize and the next measure.
 *
 * Columns never share a photo. If the photos would run out, the wall
 * drops a column: tiles get wider and taller, so each column needs
 * fewer.
 */
export function planAuthWall(width: number, height: number, available: number, preferredColumns: number): WallPlan {
  const { pad, gap, tileRatio } = WALL_GEOMETRY;
  const needed = (columns: number) => {
    const colW = Math.max(1, (width - 2 * pad - (columns - 1) * gap) / columns);
    const tileH = colW * tileRatio;
    return Math.floor((Math.max(0, height) + tileH) / (tileH + gap)) + 2;
  };
  let columns = Math.max(1, Math.floor(preferredColumns));
  let perColumn = needed(columns);
  while (columns > 1 && columns * perColumn > available) {
    columns -= 1;
    perColumn = needed(columns);
  }
  return { columns, perColumn: Math.max(1, Math.min(perColumn, Math.floor(available / columns))) };
}

/** Column `c` of the wall: every `columns`-th photo, so columns never overlap and growing a column only adds to its end. */
export const wallColumn = (photos: readonly string[], plan: WallPlan, c: number): string[] =>
  Array.from({ length: plan.perColumn }, (_, j) => photos[c + j * plan.columns]).filter(Boolean);
